import React from "react";
import { Helmet } from "react-helmet";
import { FileText, Scale, AlertCircle, ShieldCheck, Shield, Clock, CreditCard, Truck, Home, UserCheck, Phone, Mail, MapPin, Calendar, CheckCircle, XCircle } from "lucide-react";

const TermsOfService = () => {
  const effectiveDate = "January 1, 2024";
  const companyName = "Knight Cleaning";
  const companyAddress = "Melbourne VIC, Australia";
  const contactEmail = "legal@knightcleanings.com.au";
  const contactPhone = "+61 420 499 661";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>Terms of Service | Knight Cleaning</title>
        <meta name="description" content="Terms of Service for Knight Cleaning. Read our service terms, booking policies, payment terms, and customer responsibilities for professional cleaning services." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-800 via-indigo-800 to-purple-800 text-white py-16 md:py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Scale size={18} />
              <span className="font-semibold">Legal Terms</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Terms of <span className="bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent">Service</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">Knight Cleaning Legal Agreement</h2>
            <p className="text-xl text-blue-100 mb-8">
              Legal agreement governing your use of Knight Cleaning services
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Calendar size={16} />
                <span>Effective: {effectiveDate}</span>
              </div>
              <div className="h-4 w-px bg-white/30" />
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <FileText size={16} />
                <span>Last Updated: {effectiveDate}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl shadow-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                <AlertCircle size={24} className="text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">Important Legal Notice</h3>
              <p className="text-amber-100">
                By booking our services, you agree to be bound by these Terms of Service. Please read them carefully before proceeding. These terms constitute a legally binding agreement between you and {companyName}.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          {/* Table of Contents */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <FileText size={24} className="text-blue-600" />
              Table of Contents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { id: "acceptance", title: "1. Acceptance of Terms" },
                { id: "services", title: "2. Services Description" },
                { id: "booking", title: "3. Booking & Scheduling" },
                { id: "pricing", title: "4. Pricing & Payment" },
                { id: "cancellation", title: "5. Cancellation Policy" },
                { id: "customer", title: "6. Customer Responsibilities" },
                { id: "liability", title: "7. Liability & Insurance" },
                { id: "warranty", title: "8. Service Warranty" },
                { id: "intellectual", title: "9. Intellectual Property" },
                { id: "privacy", title: "10. Privacy Policy" },
                { id: "termination", title: "11. Termination" },
                { id: "governing", title: "12. Governing Law" },
              ].map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-4 py-3 rounded-lg border border-gray-200 transition-all hover:border-blue-300 hover:shadow-sm"
                >
                  {item.title}
                </a>
              ))}
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-16">
            {/* 1. Acceptance of Terms */}
            <section id="acceptance" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center">
                  <CheckCircle size={24} className="text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
                  <p className="text-gray-600">Effective: {effectiveDate}</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Agreement to Terms</h3>
                  <p className="text-gray-700 mb-4">
                    These Terms of Service ("Terms") govern your access to and use of the cleaning services provided by {companyName} ("we," "us," or "our"). By booking, scheduling, or using our services, you agree to be bound by these Terms.
                  </p>
                  <div className="bg-blue-50 rounded-xl p-5">
                    <h4 className="font-semibold text-blue-900 mb-2">Binding Agreement</h4>
                    <p className="text-blue-800 text-sm">
                      These Terms form a legally binding contract between you (the "Customer") and {companyName}. If you are booking on behalf of a company or organization, you represent that you have the authority to bind that entity to these Terms.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Modification of Terms</h3>
                  <p className="text-gray-700">
                    We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after changes constitutes acceptance of the modified Terms.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Eligibility Requirements</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-green-600" />
                      </div>
                      <span className="text-gray-700">You must be at least 18 years old to book services</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-green-600" />
                      </div>
                      <span className="text-gray-700">You must have legal authority to authorize services at the property</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-green-600" />
                      </div>
                      <span className="text-gray-700">You must provide accurate and complete information</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 2. Services Description */}
            <section id="services" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center">
                  <Home size={24} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. Services Description</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Scope</h3>
                  <p className="text-gray-700 mb-6">
                    {companyName} provides professional cleaning services for residential and commercial properties in Melbourne and surrounding areas. Our services include but are not limited to:
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Residential Services</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                          Regular maintenance cleaning
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                          Deep cleaning services
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                          End of lease cleaning
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-blue-600" />
                          Carpet & upholstery cleaning
                        </li>
                      </ul>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Commercial Services</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                          Office cleaning
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                          Industrial cleaning
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                          Emergency restoration
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-600" />
                          Window & gutter cleaning
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="font-semibold text-yellow-900 mb-3">Service Limitations</h3>
                  <p className="text-yellow-800 mb-3">
                    Our services do NOT include:
                  </p>
                  <ul className="space-y-2 text-yellow-800">
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Structural repairs or renovations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Pest control or extermination services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Moving or lifting heavy furniture (over 25kg)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>Handling hazardous materials or biohazards</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Modifications</h3>
                  <p className="text-gray-700">
                    We reserve the right to modify or discontinue any service at any time. If we make significant changes to a service you have booked, we will notify you and provide options for alternative arrangements or refunds.
                  </p>
                </div>
              </div>
            </section>

            {/* 3. Booking & Scheduling */}
            <section id="booking" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center">
                  <Calendar size={24} className="text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. Booking & Scheduling</h2>
              </div>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Process</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Service Request</h4>
                        <p className="text-gray-700">Submit booking request via website, phone, or WhatsApp</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Confirmation</h4>
                        <p className="text-gray-700">Receive booking confirmation with date, time, and quote</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Payment Authorization</h4>
                        <p className="text-gray-700">Provide payment authorization as per terms</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Service Delivery</h4>
                        <p className="text-gray-700">Our team arrives at scheduled time for service delivery</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Scheduling Terms</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Time Windows</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center gap-2">
                          <Clock size={16} className="text-gray-400" />
                          <span>Morning: 9:00 AM - 12:00 PM</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Clock size={16} className="text-gray-400" />
                          <span>Afternoon: 1:00 PM - 4:00 PM</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Clock size={16} className="text-gray-400" />
                          <span>Evening: 5:00 PM - 8:00 PM</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Scheduling Notes</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center gap-2">
                          <AlertCircle size={16} className="text-amber-500" />
                          <span>48-hour notice for rescheduling</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <AlertCircle size={16} className="text-amber-500" />
                          <span>Same-day booking subject to availability</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <AlertCircle size={16} className="text-amber-500" />
                          <span>Holiday surcharges may apply</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-3">Access Requirements</h3>
                  <p className="text-blue-800 mb-3">
                    Customer must provide safe and unobstructed access to all areas requiring cleaning. This includes:
                  </p>
                  <ul className="space-y-2 text-blue-800">
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span>Clear access to all rooms and areas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span>Secure entry arrangements (key, code, or on-site)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span>Safe working environment free of hazards</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 4. Pricing & Payment */}
            <section id="pricing" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 flex items-center justify-center">
                  <CreditCard size={24} className="text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. Pricing & Payment</h2>
              </div>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Structure</h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Service Type</th>
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Basis</th>
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Minimum</th>
                          <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Residential Cleaning</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Per hour/room</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">$129</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">2-hour minimum</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Commercial Cleaning</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Per square meter</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">$299</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Custom quote</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Carpet Cleaning</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Per room</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">$79</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Based on size</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Emergency Service</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">Call-out + hourly</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">$299</td>
                          <td className="border border-gray-200 px-4 py-3 text-gray-700">After-hours premium</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Terms</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Payment Methods</h4>
                      <ul className="space-y-3">
                        <li className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="font-bold text-sm text-blue-600">CC</span>
                          </div>
                          <span className="text-gray-700">Credit/Debit Card</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <span className="font-bold text-sm text-green-600">PP</span>
                          </div>
                          <span className="text-gray-700">PayPal</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="font-bold text-sm text-purple-600">BT</span>
                          </div>
                          <span className="text-gray-700">Bank Transfer</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                            <span className="font-bold text-sm text-amber-600">C</span>
                          </div>
                          <span className="text-gray-700">Cash (On-site only)</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Payment Schedule</h4>
                      <ul className="space-y-3">
                        <li className="flex items-center justify-between">
                          <span className="text-gray-700">One-time Service</span>
                          <span className="font-semibold text-gray-900">Payment on completion</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span className="text-gray-700">Recurring Service</span>
                          <span className="font-semibold text-gray-900">Monthly invoicing</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span className="text-gray-700">Commercial Service</span>
                          <span className="font-semibold text-gray-900">7-day terms</span>
                        </li>
                        <li className="flex items-center justify-between">
                          <span className="text-gray-700">Emergency Service</span>
                          <span className="font-semibold text-gray-900">50% deposit required</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="font-semibold text-red-900 mb-3">Additional Charges</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-red-800 mb-2">Extra Services</h4>
                      <p className="text-red-700 text-sm">
                        Additional cleaning requirements identified on-site may incur extra charges. We will seek your approval before proceeding with additional services.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-red-800 mb-2">Late Payment Fees</h4>
                      <p className="text-red-700 text-sm">
                        Accounts overdue by more than 30 days will incur a late fee of 2% per month on the outstanding balance.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-red-800 mb-2">Cancellation Fees</h4>
                      <p className="text-red-700 text-sm">
                        Late cancellations (less than 24 hours notice) may incur a cancellation fee of up to 50% of the service cost.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 5. Cancellation Policy */}
            <section id="cancellation" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-red-100 to-pink-100 flex items-center justify-center">
                  <XCircle size={24} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. Cancellation Policy</h2>
              </div>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancellation Timeframes</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4 p-4 border border-green-200 rounded-xl bg-green-50">
                      <div className="h-10 w-10 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0">
                        <span className="font-bold">24h</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-900 mb-1">More than 24 hours notice</h4>
                        <p className="text-green-800">Full refund or reschedule without penalty</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 p-4 border border-yellow-200 rounded-xl bg-yellow-50">
                      <div className="h-10 w-10 rounded-full bg-yellow-600 text-white flex items-center justify-center flex-shrink-0">
                        <span className="font-bold">4h</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-yellow-900 mb-1">4-24 hours notice</h4>
                        <p className="text-yellow-800">50% cancellation fee applies</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 p-4 border border-red-200 rounded-xl bg-red-50">
                      <div className="h-10 w-10 rounded-full bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                        <span className="font-bold">&lt;4h</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-900 mb-1">Less than 4 hours notice</h4>
                        <p className="text-red-800">100% cancellation fee applies</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-3">Cancellation Process</h3>
                  <p className="text-blue-800 mb-4">
                    To cancel a scheduled service, you must:
                  </p>
                  <ul className="space-y-3 text-blue-800">
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">1</span>
                      </div>
                      <span>Contact us via phone, email, or WhatsApp</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">2</span>
                      </div>
                      <span>Provide your booking reference number</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">3</span>
                      </div>
                      <span>Receive cancellation confirmation from us</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-white flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">4</span>
                      </div>
                      <span>Allow 5-10 business days for refund processing</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Force Majeure</h3>
                  <p className="text-gray-700 mb-3">
                    Neither party shall be liable for any failure or delay in performing obligations under these Terms if such failure or delay is due to circumstances beyond their reasonable control ("Force Majeure"), including but not limited to:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Natural disasters (floods, earthquakes, bushfires)</li>
                    <li>Government actions or restrictions</li>
                    <li>Pandemic or epidemic conditions</li>
                    <li>Utility failures or infrastructure issues</li>
                    <li>Labor disputes or strikes</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 6. Customer Responsibilities */}
            <section id="customer" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-teal-100 to-emerald-100 flex items-center justify-center">
                  <UserCheck size={24} className="text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. Customer Responsibilities</h2>
              </div>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pre-Service Requirements</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Access & Security</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Provide safe access to the property</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Secure pets during service hours</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Notify of any security systems</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Preparation</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Clear surfaces of personal items</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Identify special requirements</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span>Provide parking arrangements if needed</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h3 className="font-semibold text-amber-900 mb-3">Important Notices</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-amber-800 mb-2">Valuables & Sentimental Items</h4>
                      <p className="text-amber-700 text-sm">
                        We are not responsible for valuables, cash, jewelry, or sentimental items left out during cleaning. Please secure these items before our arrival.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-800 mb-2">Damage Disclosure</h4>
                      <p className="text-amber-700 text-sm">
                        Please disclose any existing damage or fragile items before service begins. Failure to do so may limit our liability for accidental damage.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-800 mb-2">Health & Safety</h4>
                      <p className="text-amber-700 text-sm">
                        Notify us of any health conditions (allergies, respiratory issues) or safety concerns that may affect our staff during service.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Post-Service Responsibilities</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span>Inspect service upon completion and report any concerns within 24 hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span>Make payment as per agreed terms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span>Provide feedback for service improvement</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 7. Liability & Insurance */}
            <section id="liability" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center">
                  <Shield size={24} className="text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. Liability & Insurance</h2>
              </div>
              
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6">
                  <h3 className="font-semibold text-indigo-900 mb-4">Insurance Coverage</h3>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl font-bold text-indigo-600">$2M</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Public Liability</h4>
                      <p className="text-sm text-gray-600">Coverage for property damage or personal injury</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl font-bold text-indigo-600">$1M</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Professional Indemnity</h4>
                      <p className="text-sm text-gray-600">Coverage for professional errors or omissions</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <Truck size={24} className="text-indigo-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Workers Compensation</h4>
                      <p className="text-sm text-gray-600">Coverage for our staff while on your property</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Limitation of Liability</h3>
                  
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">Direct Damages</h4>
                      <p className="text-gray-700">
                        Our total liability for any claim arising from or related to these Terms or our services shall not exceed the total amount paid by you for the specific service giving rise to the claim.
                      </p>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">Indirect Damages</h4>
                      <p className="text-gray-700">
                        We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses.
                      </p>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">Exclusions</h4>
                      <p className="text-gray-700">
                        Our liability is excluded for damage resulting from: pre-existing conditions, improper preparation, failure to disclose hazards, acts of third parties, or force majeure events.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="font-semibold text-red-900 mb-3">Damage Claims Process</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Report Immediately</h4>
                        <p className="text-red-700 text-sm">Notify us of any damage within 24 hours of service completion</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Provide Documentation</h4>
                        <p className="text-red-700 text-sm">Submit photos and description of the damage</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Investigation</h4>
                        <p className="text-red-700 text-sm">We will investigate within 7 business days</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Resolution</h4>
                        <p className="text-red-700 text-sm">We will propose repair, replacement, or compensation</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 8. Service Warranty */}
            <section id="warranty" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 flex items-center justify-center">
                  <ShieldCheck size={24} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. Service Warranty</h2>
              </div>
              
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                  <h3 className="font-semibold text-green-900 mb-4">Satisfaction Guarantee</h3>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">🔍</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">24-Hour Inspection</h4>
                      <p className="text-sm text-gray-600">Report any issues within 24 hours of service</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">🔄</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Free Re-Clean</h4>
                      <p className="text-sm text-gray-600">We'll return to fix any missed areas at no cost</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">💰</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Refund Option</h4>
                      <p className="text-sm text-gray-600">Full refund if we cannot resolve to your satisfaction</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Warranty Limitations</h3>
                  
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Warranty Exclusions</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-2">
                        <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>Issues reported more than 24 hours after service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>Pre-existing conditions or damage</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>Issues caused by customer or third parties after service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>Normal wear and tear or aging of materials</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>Issues beyond our reasonable control</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Making a Warranty Claim</h3>
                  <p className="text-gray-700 mb-4">
                    To make a warranty claim under our Satisfaction Guarantee:
                  </p>
                  <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                    <li>Contact us within 24 hours of service completion</li>
                    <li>Provide specific details of the concern with photos if possible</li>
                    <li>Allow access for re-inspection within 48 hours</li>
                    <li>We will schedule a re-clean at the earliest available time</li>
                    <li>If still unsatisfied after re-clean, request a refund within 7 days</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* 9. Intellectual Property */}
            <section id="intellectual" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-orange-100 to-red-100 flex items-center justify-center">
                  <FileText size={24} className="text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">9. Intellectual Property</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Ownership Rights</h3>
                  <p className="text-gray-700">
                    All intellectual property rights in our services, website, content, branding, and materials ("Our IP") are owned by or licensed to {companyName}. You are granted a limited, non-exclusive, non-transferable license to use Our IP solely for the purpose of receiving our services.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Restrictions</h3>
                  <p className="text-gray-700 mb-3">You may not:</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Copy, modify, or create derivative works of Our IP</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Use Our IP for commercial purposes without permission</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Remove any copyright or proprietary notices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <span>Reverse engineer or attempt to extract source code</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Content</h3>
                  <p className="text-gray-700">
                    By providing feedback, reviews, or other content to us, you grant {companyName} a perpetual, royalty-free license to use, modify, and display such content for business purposes, including marketing and service improvement.
                  </p>
                </div>
              </div>
            </section>

            {/* 10. Privacy Policy */}
            <section id="privacy" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 flex items-center justify-center">
                  <Shield size={24} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">10. Privacy Policy</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Privacy Compliance</h3>
                  <p className="text-gray-700 mb-4">
                    Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
                  </p>
                  <div className="bg-blue-50 rounded-xl p-5">
                    <h4 className="font-semibold text-blue-900 mb-2">Privacy Policy</h4>
                    <p className="text-blue-800">
                      Please review our full Privacy Policy at <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">knightcleanings.com.au/privacy</a> to understand how we handle your personal information.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Protection</h3>
                  <p className="text-gray-700">
                    We comply with the Australian Privacy Principles and the Privacy Act 1988 (Cth). We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, disclosure, or destruction.
                  </p>
                </div>
              </div>
            </section>

            {/* 11. Termination */}
            <section id="termination" className="scroll-mt-20 border-b border-gray-200 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                  <XCircle size={24} className="text-gray-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">11. Termination</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Termination Rights</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">By Customer</h4>
                      <p className="text-gray-700">
                        You may terminate recurring services by providing 30 days written notice. One-time services may be cancelled as per our cancellation policy.
                      </p>
                    </div>
                    
                    <div className="border border-gray-200 rounded-xl p-5">
                      <h4 className="font-semibold text-gray-900 mb-2">By {companyName}</h4>
                      <p className="text-gray-700">
                        We may terminate services immediately if you breach these Terms, fail to pay, or engage in unsafe or abusive behavior toward our staff.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Effects of Termination</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-gray-600" />
                      </div>
                      <span>All rights and licenses granted under these Terms will immediately cease</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-gray-600" />
                      </div>
                      <span>Outstanding payments become immediately due</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-gray-600" />
                      </div>
                      <span>We may delete or anonymize your account information</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 12. Governing Law */}
            <section id="governing" className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center">
                  <Scale size={24} className="text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">12. Governing Law & Dispute Resolution</h2>
              </div>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Governing Law</h3>
                  <p className="text-gray-700">
                    These Terms shall be governed by and construed in accordance with the laws of Victoria, Australia, without regard to its conflict of law principles.
                  </p>
                </div>

                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="font-semibold text-purple-900 mb-4">Dispute Resolution Process</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-900 mb-1">Informal Negotiation</h4>
                        <p className="text-purple-800">Parties shall attempt to resolve disputes informally within 30 days</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-900 mb-1">Mediation</h4>
                        <p className="text-purple-800">If unresolved, parties shall attempt mediation through an accredited mediator</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-900 mb-1">Legal Proceedings</h4>
                        <p className="text-purple-800">If mediation fails, parties may pursue legal action in Victorian courts</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Class Action Waiver</h3>
                  <p className="text-gray-700">
                    You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-3">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail size={18} className="text-blue-600" />
                      <span className="text-blue-800">{contactEmail}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={18} className="text-blue-600" />
                      <span className="text-blue-800">{contactPhone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin size={18} className="text-blue-600" />
                      <span className="text-blue-800">{companyAddress}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Final Acknowledgment */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Acknowledgement of Terms</h3>
              <div className="max-w-2xl mx-auto">
                <p className="text-gray-700 mb-6 text-center">
                  By booking our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these Terms, you may not use our services.
                </p>
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Document Information</h4>
                      <p className="text-sm text-gray-600">Terms of Service v2.0</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Effective Date</p>
                      <p className="font-semibold text-gray-900">{effectiveDate}</p>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-600">
                      These Terms supersede all prior agreements and understandings between you and {companyName} regarding our services.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Download Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Need a Copy?</h3>
              <p className="text-gray-600">Download or print these Terms for your records</p>
            </div>
            <div className="flex gap-4">
              <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center gap-2">
                <FileText size={18} />
                <span>Download PDF</span>
              </button>
              <button className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                <span>🖨️</span>
                <span>Print Version</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;